/* @flow */
import invariant from 'invariant';
import * as React from 'react';
import { partition } from 'lodash';

import AffiliationStore from 'stores/AffiliationStore';
import BoxBody from 'components/BoxBody.jsx';
import Button from 'components/Button.jsx';
import CurrentUser from 'auth/CurrentUser';
import Dialog from 'components/Dialog.jsx';
import { FormattedMessage } from 'react-intl';
import Group from 'entities/Group';
import GroupName from 'groups/components/GroupName.jsx';
import GroupStore from 'groups/stores/GroupStore';
import OrganizationStore from 'stores/OrganizationStore';
import ModalTrigger from 'components/ModalTrigger.jsx';
import SettingsRemoveSchoolModal from 'settings/components/SettingsRemoveSchoolModal.jsx';
import './styles/SchoolSettingsModal.scss';

class SchoolSettingsModal extends React.Component<{
  group: Group,
  onRequestHide?: () => any, // injected prop, not actually optional
  onLeave?: () => any,
}> {
  renderLeaveSchoolModal() {
    const { group, onRequestHide, onLeave } = this.props;
    const orgId = group.getStaffClassOrgId();
    invariant(orgId, 'SchoolSettingsModal renderLeaveSchoolModal missing orgId');
    const orgGroups = GroupStore.getGroupsAtOrganization(orgId).models;
    const groupsByOwned = partition(orgGroups, (g: Group) =>
      g.isOwnedByUser(CurrentUser.getUserId())
    );
    const affiliation = AffiliationStore.getAffiliation(orgId);
    invariant(affiliation, 'SchoolSettingsModal missing affiliation');
    const org = OrganizationStore.getOrganization(orgId);
    invariant(org, 'SchoolSettingsModal missing org');
    return (
      <SettingsRemoveSchoolModal
        affiliation={affiliation}
        ownedGroups={groupsByOwned[0]}
        subscribedGroups={groupsByOwned[1]}
        school={org}
        onConfirm={() => {
          if (onRequestHide) {
            onRequestHide();
          }
          if (onLeave) {
            onLeave();
          }
        }}
      />
    );
  }

  render() {
    const { group } = this.props;
    const isGroupOwner = group.isOwnedByUser(CurrentUser.getUserId());

    const ownerNames = group.getOwners().map(o => o.getUser().getName());

    return (
      <Dialog
        eventScreenName="SchoolSettingsModal"
        {...this.props}
        size="small"
        title="School settings"
        className="SchoolSettingsModal">
        <BoxBody divided>
          <div>
            <div>
              <div className="SchoolSettingsModal-info-header">School name</div>
              <div className="SchoolSettingsModal-info-content">
                <GroupName group={group} />
              </div>
            </div>
            <div>
              <div className="SchoolSettingsModal-info-header">School code</div>
              <div className="SchoolSettingsModal-info-content">@{group.getCodeOrError()}</div>
            </div>
            <div>
              <div className="SchoolSettingsModal-info-header">
                <FormattedMessage
                  id="groupSchoolDialog.title"
                  defaultMessage="{adminNum, plural, one {Admin} other {Admins}}"
                  values={{ adminNum: ownerNames.length }}
                />
              </div>
              <div className="SchoolSettingsModal-info-content">{ownerNames.join(', ')}</div>
            </div>
          </div>
          {isGroupOwner && (
            <div className="SchoolSettingsModal-searchable">
              <div className="SchoolSettingsModal-searchable-explanation">
                As a verified administrator, you can see and message everyone at your school.
                Everyone else can only see the teachers.
              </div>

              <div className="SchoolSettingsModal-searchable-explanation">
                Teachers can decide whether students and parents can message them and whether people
                in their classes can message each other.
              </div>
            </div>
          )}
          {group.canLeave() && (
            <div className="SchoolSettingsModal-actionBox">
              <ModalTrigger modal={this.renderLeaveSchoolModal()}>
                <Button
                  data-test-selector="leave_class_button"
                  size="small"
                  level="text"
                  color="lava">
                  Leave this school
                </Button>
              </ModalTrigger>
              <p>You will no longer receive announcements from this school.</p>
            </div>
          )}
        </BoxBody>
      </Dialog>
    );
  }
}

export default SchoolSettingsModal;
